import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  IconShapeCrossCircle,
  IconShapeTick,
  IconCoreWarning,
} from '@xingternal/malt-icon';
import testable from '@xingternal/malt-util/testable';
import { QA_MALT } from '@xingternal/malt-constants';
import styles from './Input.css';
import CircleLoader from './CircleLoader';

const TestableDiv = testable(QA_MALT, 'div');

/**
 * You can pass any HTML attributes and any handler into `@xingternal/malt-input`.
 * Some were explicitly allowed in earlier versions, but we decided to
 * simply pass them through to the input field.
 *
 * Please check the explicitly defined props as well – some toggle
 * CSS classes or are needed for specific actions within the input (e.g. `disabled`).
 * They shouldn't get into your way in any case, though.
 */

const Input = ({
  className = undefined,
  onClear = undefined,
  value = undefined,
  disabled = undefined,
  hasError = undefined,
  size = 's',
  sizeConfined = undefined,
  sizeWide = undefined,
  icon: IconComp = undefined,
  testId = undefined,
  inputRef = undefined,
  valid = undefined,
  ...inputProps
}) => {
  const showClearButton = onClear && !disabled && value;
  const inputClasses = classnames(className, styles.input, styles[size], {
    [styles[`confined-${sizeConfined}`]]: sizeConfined,
    [styles[`wide-${sizeWide}`]]: sizeWide,
    [styles.invalid]: hasError,
    [styles.withClearButton]: showClearButton,
  });

  return (
    <TestableDiv className={styles.inputWrap} testId={testId}>
      <input
        {...inputProps}
        ref={inputRef}
        value={value}
        disabled={disabled}
        className={inputClasses}
      />
      {!showClearButton &&
        ((IconComp && (
          <span className={classnames(styles.initIcon, styles.iconContainer)}>
            <IconComp className={styles.icon} />
          </span>
        )) ||
          (valid && (
            <span className={classnames(styles.valid, styles.iconContainer)}>
              <CircleLoader>
                <IconShapeTick className={styles.icon} />
              </CircleLoader>
            </span>
          )) ||
          (hasError && (
            <span
              className={classnames(styles.errorIcon, styles.iconContainer)}
            >
              <IconCoreWarning className={styles.icon} />
            </span>
          )))}
      <button
        tabIndex="-1"
        type="button"
        onClick={onClear}
        className={classnames(
          showClearButton
            ? [styles.clearButton, styles.iconContainer]
            : styles.hidden
        )}
      >
        <IconShapeCrossCircle className={styles.icon} />
      </button>
    </TestableDiv>
  );
};

Input.displayName = 'Input';

Input.propTypes = {
  /** add a custom className to the component */
  className: PropTypes.string,
  /** callback for the clear button */
  onClear: PropTypes.func,
  /** contains the content of the input */
  value: PropTypes.string,
  /** define an icon that should be placed within the input */
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  /** disabled the input */
  disabled: PropTypes.bool,
  /** set the mobile and up size for input (s, m, l) */
  size: PropTypes.oneOf(['s', 'm', 'l']),
  /** set the tablet and up size for input (s, m, l) */
  sizeConfined: PropTypes.oneOf(['s', 'm', 'l']),
  /** set the desktop size for input (s, m, l) */
  sizeWide: PropTypes.oneOf(['s', 'm', 'l']),
  /** toggle error state of the input */
  hasError: PropTypes.bool,
  /** toggle valid state to the input */
  valid: PropTypes.bool,
  /** define a testid for the input */
  testId: PropTypes.string,
  /** callback to store a reference to the DOM element */
  inputRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
};

export default Input;
